// The staff strip of plan §6 step 4: the claimed adult's enrolment photo next to the live camera,
// the staff member on duty, and any restriction as the amber RESTRICTION banner.
import { useEffect, useRef } from "react";
import { Banner, PhotoTile } from "@vavr/ui";
import type { Roster } from "@vavr/client";
import { usePhoto, type PhotoSource } from "../photos.ts";
import type { Restriction } from "./machine.ts";

export function LiveVideo({ stream, label, className }: { stream: MediaStream | null; label: string; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || !stream) return;
    v.srcObject = stream;
    v.play().catch(() => undefined);
    return () => { v.srcObject = null; };
  }, [stream]);
  if (!stream) return <div className={`pmk-video pmk-video--off ${className ?? ""}`} role="img" aria-label="Camera is off">Camera off</div>;
  return <video ref={ref} className={`pmk-video ${className ?? ""}`} muted playsInline aria-label={label} />;
}

const KIND: Record<string, string> = { court: "Court order", protective: "Protective order", site: "Site decision" };

export function restrictionTitle(r: Restriction, roster: Roster) {
  const a = r.subject_adult_id ? roster.adults.find((x) => x.id === r.subject_adult_id) : null;
  const who = a ? `${a.first} ${a.last}` : (r.subject_name ?? "a named person");
  const child = roster.children.find((c) => c.id === r.child_id);
  return `${r.never_release ? "Do not release" : "Check before release"}: ${who}${child ? ` (for ${child.first})` : ""}`;
}

export function RestrictionBanners({ restrictions, nameMatches, roster }: { restrictions: Restriction[]; nameMatches: Restriction[]; roster: Roster }) {
  return (
    <>
      {restrictions.map((r) => (
        <Banner key={r.id} tone="restriction" title={restrictionTitle(r, roster)}>
          {[KIND[r.kind] ?? r.kind, nameMatches.some((m) => m.id === r.id) ? "The name given matches this person." : null, r.notes]
            .filter(Boolean).join(". ")}
        </Banner>
      ))}
    </>
  );
}

export interface StaffStripProps {
  adult: { first: string; last: string; photo_sha256: string | null; relation?: string } | null;
  someoneName: string | null;
  /** null on the self-service kiosk, which nobody staffs. */
  staffName: string | null;
  stream: MediaStream | null;
  photos: PhotoSource;
  restrictions: Restriction[];
  nameMatches: Restriction[];
  roster: Roster;
  /** Under the live camera: "Now", or the face-check hint while signing. */
  caption?: string;
}

export function StaffStrip({ adult, someoneName, staffName, stream, photos, restrictions, nameMatches, roster, caption = "Now" }: StaffStripProps) {
  const src = usePhoto(photos, adult?.photo_sha256);
  const claimed = adult || someoneName;
  return (
    <aside className="pmk-strip" aria-label="For staff">
      <div className={`pmk-strip__faces ${claimed ? "" : "pmk-strip__faces--live"}`}>
        {adult && <PhotoTile name={`${adult.first} ${adult.last}`} detail={adult.relation ?? "On file"} src={src} className="pmk-strip__photo" />}
        {!adult && someoneName && <PhotoTile name={someoneName} detail="Not on the list" className="pmk-strip__photo" />}
        <figure className="pmk-strip__live">
          <LiveVideo stream={stream} label="Live camera" />
          <figcaption className="pmk-strip__caption" aria-live="polite">{caption}</figcaption>
        </figure>
      </div>
      {staffName && <p className="pmk-strip__staff">{`Staff: ${staffName}`}</p>}
      <RestrictionBanners restrictions={restrictions} nameMatches={nameMatches} roster={roster} />
    </aside>
  );
}
